<template>
  <div class="p-6">
    <div v-if="loading" class="text-gray-500">Loading survey...</div>
    <div v-else-if="error" class="text-red-600">{{ error }}</div>
    <div v-else-if="survey">
      <!-- Title -->
      <h1 v-if="!isEditing" class="text-2xl font-bold mb-4 text-blue-600">
        {{ survey.title }}
      </h1>
      <input
        v-else
        v-model="survey.title"
        type="text"
        class="border p-2 rounded w-full mb-4"
      />

      <!-- Details Card -->
      <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        <table class="w-full text-left">
          <tbody>
            <tr>
              <td class="font-medium text-gray-600 py-2 w-40">ID</td>
              <td class="py-2">{{ survey.id }}</td>
            </tr>
            <tr>
              <td class="font-medium text-gray-600 py-2">Start Date</td>
              <td class="py-2">
                <span v-if="!isEditing">{{ formatDate(survey.start_date) }}</span>
                <input v-else type="date" v-model="survey.start_date" class="border p-1 rounded" />
              </td>
            </tr>
            <tr>
              <td class="font-medium text-gray-600 py-2">End Date</td>
              <td class="py-2">
                <span v-if="!isEditing">{{ formatDate(survey.end_date) }}</span>
                <input v-else type="date" v-model="survey.end_date" class="border p-1 rounded" />
              </td>
            </tr>
            <tr v-if="survey.description">
              <td class="font-medium text-gray-600 py-2 align-top">Description</td>
              <td class="py-2">
                <span v-if="!isEditing">{{ survey.description }}</span>
                <textarea
                  v-else
                  v-model="survey.description"
                  class="border p-2 rounded w-full"
                ></textarea>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Questions Section -->
<div class="mt-6">
  <h2 class="text-xl font-semibold mb-3">Questions</h2>
  <ul>
    <li
      v-for="(question, index) in survey.questions"
      :key="question.id"
      class="mb-4 p-3 border rounded bg-gray-50"
    >
      <div v-if="!isEditing">
        <p class="font-medium">{{ index + 1 }}. {{ question.question_text }}</p>
        <p class="text-sm text-gray-600">
          Type: <span class="font-semibold">{{ question.question_type }}</span>
        </p>
        <div v-if="question.options && question.options.length">
          <p class="text-sm text-gray-600">Options:</p>
          <ul class="list-disc list-inside ml-4 text-gray-700">
            <li v-for="(opt, i) in question.options" :key="i">{{ opt }}</li>
          </ul>
        </div>
      </div>

      <!-- Editing mode -->
      <div v-else>
        <!-- Question Text -->
        <label class="block text-sm font-medium text-gray-600">Question</label>
        <input
          v-model="question.question_text"
          type="text"
          class="border p-2 rounded w-full mb-2"
        />

        <!-- Question Type -->
        <label class="block text-sm font-medium text-gray-600">Type</label>
        <select
          v-model="question.question_type"
          class="border p-2 rounded w-full mb-2"
        >
          <option value="text">Text</option>
          <option value="multiple">Multiple Choice</option>
          <option value="checkbox">Checkbox</option>
          <option value="dropdown">Dropdown</option>
        </select>

        <!-- Options (for multiple, checkbox, dropdown only) -->
        <div v-if="['multiple','checkbox','dropdown'].includes(question.question_type)">
          <label class="block text-sm font-medium text-gray-600">Options (comma separated)</label>
          <textarea
            v-model="question.optionsString"
            class="border p-2 rounded w-full"
            placeholder="Enter options separated by commas"
          ></textarea>
        </div>
      </div>
    </li>
  </ul>
</div>


      <!-- Action Buttons -->
      <div class="mt-6 flex gap-3">
        <button
          v-if="!isEditing"
          class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
          @click="isEditing = true"
        >
          ✏️ Edit
        </button>

        <div v-else>
          <button
            class="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 mr-2"
            @click="saveSurvey"
          >
            Save
          </button>
          <button
            class="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
            @click="cancelEdit"
          >
            ✖ Cancel
          </button>
        </div>

        <button
          class="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 ml-auto"
          @click="back"
        >
          ← Back to Survey List
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";

export default {
  name: "SurveyDetail",
  data() {
    return {
      survey: null,
      originalSurvey: null,
      loading: true,
      error: null,
      isEditing: false,
    };
  },
  methods: {
    back() {
      this.$router.push({ name: "surveys" });
    },
    async fetchSurvey() {
  const { id } = this.$route.params;
  try {
    const response = await axios.get(
      `https://apiresources.brandfi.co.ke/api/v1/survey/${id}`
    );
    // Transform options into string for editing
    response.data.questions.forEach(q => {
      if (Array.isArray(q.options)) {
        q.optionsString = q.options.join(", ");
      } else {
        q.optionsString = "";
      }
    });
    this.survey = response.data;
    this.originalSurvey = JSON.parse(JSON.stringify(response.data));
  } catch (err) {
    console.error(err);
    this.error = "Failed to load survey.";
  } finally {
    this.loading = false;
  }
},
    async saveSurvey() {   

      try {
     // ✅ Date validation
    if (new Date(this.survey.start_date) > new Date(this.survey.end_date)) {
      this.$root.$emit('alert_error', [
        "Start date cannot be later than End date!"
      ]);
      return; // Stop save
    }

      // Make sure all question.options are arrays
    if (this.survey.questions) {
      this.survey.questions = this.survey.questions.map(q => {
        let options = q.optionsString;

        // If options is a string, try to parse it
        if (typeof options === "string") {
          try {
            options = JSON.parse(options);
          } catch (e) {
            // fallback: split by commas if it's just a plain string
            options = options.split(",").map(o => o.trim());
          }
        }

        return {
          ...q,
          options: Array.isArray(options) ? options : [], // ensure it's always an array
        };
      });
    }


    console.log("Normalized survey before sending:", this.survey);
        const response = await axios.put(
          `https://apiresources.brandfi.co.ke/api/v1/survey/${this.survey.id}`,
          this.survey
        );

       // this.survey = response.data;
       this.$root.$emit('alert_success',["Survey Edited successfully!"]);
        this.fetchSurvey();
        this.originalSurvey = JSON.parse(JSON.stringify(response.data));
        this.isEditing = false;
        
      } catch (err) {
        console.error(err);
        alert("Failed to save survey.");
      }
    },
    cancelEdit() {
      this.survey = JSON.parse(JSON.stringify(this.originalSurvey));
      this.isEditing = false;
    },
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-CA"); // yyyy-mm-dd
    },
  },
  mounted() {
  //  this.fetchSurvey();
  },
};
</script>
